import {
  type ExportFilterPropsUnion,
  type ExportFormatType,
  type ExportIntervalType,
} from '@/components/modals/ReportSummaryModal/ReportSummaryModal'
import {
  type EmailReportFilterEvaluation,
  type EmailReportFilterPropsUnion,
  type EmailReportFilterSource,
} from '@/pages/EmailReport/components/CustomFilters/FilterComponent'
import {
  type EmailReportFormatType,
  type EmailReportIntervalType,
} from '@/pages/EmailReport/EmailReport'
import {
  type ReviewNotificationsFilterEvaluation,
  type ReviewNotificationsFilterPropsUnion,
  type ReviewNotificationsFilterSource,
  type ReviewNotificationsIntervalType,
} from '@/pages/ReviewNotification/constants'
import { type ReviewNotificationsProvidersType } from '@/utils/constants/providers/reviewNotificationsProviders'
import { type UserType } from '../mockUser'

export enum NotificationSource {
  reviews = 'reviews',
  reports = 'reports',
  exports = 'exports',
}

export enum ReportDataType {
  analytics = 'analytics',
  reviewAnalysis = 'reviewAnalysis',
  sentimentAnalysis = 'sentimentAnalysis',
  keywordAnalysis = 'keywordAnalysis',
}

interface CriteriaAttributes {
  [NotificationSource.reviews]: {
    source: NotificationSource.reviews
    reviewCriteria: ['onCreate', 'onUpdate']
  }
  [NotificationSource.reports]: {
    source: NotificationSource.reports
    reportCriteria: {
      lookback: number
      lookbackType: 'days'
      reports: ReportDataType[]
    }
  }
  [NotificationSource.exports]: {
    source: NotificationSource.exports
    exportCriteria: {
      startDate: string
      endDate: string
      reports: ReportDataType[]
    }
  }
}

interface IntervalAttributes {
  [NotificationSource.reviews]: ReviewNotificationsIntervalType
  [NotificationSource.reports]: EmailReportIntervalType
  [NotificationSource.exports]: ExportIntervalType
}

interface EmailContentAttributes {
  [NotificationSource.reviews]: {
    isCustom: boolean
    subjectOverride: string
    bodyOverride: string
  }
  [NotificationSource.reports]: {
    selectedOutputs: Array<EmailReportFormatType>
    isCustom: boolean
    subjectOverride: string
    bodyOverride: string
  }
  [NotificationSource.exports]: {
    selectedOutputs: Array<ExportFormatType>
    isCustom: boolean
  }
}

interface FiltersAttributes {
  [NotificationSource.reviews]: Array<ReviewNotificationsFilterPropsUnion>
  [NotificationSource.reports]: Array<EmailReportFilterPropsUnion>
  [NotificationSource.exports]: Array<ExportFilterPropsUnion>
}

interface RecipientsAttributes {
  [NotificationSource.reviews]: {
    externalRecipients?: Array<{ email: string }>
    users?: Array<string>
  }
  [NotificationSource.reports]: {
    externalRecipients?: Array<{ email: string }>
    users?: Array<string>
  }
  [NotificationSource.exports]: {
    externalRecipients?: Array<{ email: string }>
  }
}

interface GenericNotificationProps<T extends NotificationSource> {
  enabled: boolean
  deliveryTimeZone: string
  deliveryTime: number
  criteria: CriteriaAttributes[T]
  interval: IntervalAttributes[T]
  deliveryDay: number | null
  name: string
  description: string
  filters: FiltersAttributes[T]
  emailContent: EmailContentAttributes[T]
  recipients: RecipientsAttributes[T]
}

type ExportNotificationProps = Omit<
  GenericNotificationProps<NotificationSource.exports>,
  'deliveryTime' | 'deliveryDay' | 'name' | 'description'
>

export type CreateNotificationProps<T extends NotificationSource = NotificationSource.reviews> =
  T extends NotificationSource.exports ? ExportNotificationProps : GenericNotificationProps<T>

type NotificationChannelsProps<T extends NotificationSource> = T extends NotificationSource.reviews
  ? Array<{
      id: string
      notificationId: string
      channel: 'email'
      isCustom: boolean
      subjectOverride: string
      bodyOverride: string
    }>
  : Array<{
      id: string
      notificationId: string
      channel: 'email'
      isCustom: boolean
      subjectOverride: string
      bodyOverride: string
      selectedOutputs: Array<EmailReportFormatType>
    }>
type NotificationCriteriaProps<T extends NotificationSource> = T extends NotificationSource.reviews
  ? {
      id: string
      notificationId: string
      source: NotificationSource
      reviewCriteria: Array<string>
    }
  : {
      id: string
      notificationId: string
      source: NotificationSource
      reviewCriteria: Array<string>
      reportCriteriaId: string | null
      reportCriteria: {
        id: string
        lookback: 30 | 60 | 90
        lookbackType: 'days'
        reports: ReportDataType[]
      }
    }

export interface NotificationProps<T extends NotificationSource = NotificationSource.reviews> {
  id: string
  accountId: string
  name: string
  description: string
  interval: T extends NotificationSource.reviews
    ? ReviewNotificationsIntervalType
    : EmailReportIntervalType
  deliveryDay: number | null
  deliveryTime: number
  deliveryTimeZone: string
  enabled: boolean
  createdBy: string
  createdByUser: UserType
  createdAt: string
  updatedBy: string | null
  updatedByUser: UserType | null
  updatedAt: string
  deletedAt: string | null
  deletedBy: string | null
  recipients: {
    id: string
    notificationId: string
    externalRecipients: Array<{
      id: string
      notificationRecipientId: string
      email: string
      toName: string | null
      removed: string | null
      removedReason: string | null
    }>
    users: Array<{
      id: string
      notificationRecipientId: string
      userId: string
      removed: string | null
      removedReason: string | null
    }>
  }
  criteria: NotificationCriteriaProps<T>
  filters: Array<{
    id: string
    notificationId: string
    source: T extends NotificationSource.reviews
      ? ReviewNotificationsFilterSource
      : EmailReportFilterSource
    contents: Array<string>
    publishers: ReviewNotificationsProvidersType[]
    labels: Array<{ labelId: string; filterId: string }>
    rating: number
    recommended: boolean
    status: null // not yet implemented in API
    hasDraft: boolean
    evaluation: T extends NotificationSource.reviews
      ? ReviewNotificationsFilterEvaluation
      : EmailReportFilterEvaluation
    position: number
    createdBy: string
    createdAt: string
    updatedBy: string | null
    updatedAt: string
    folders: Array<{ filterId: string; folderId: string }>
    stores: Array<{ filterId: string; storeId: string }>
  }>
  channels: NotificationChannelsProps<T>
}
